import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@shared/ui/utils/tailwind";
import RequiredFieldIndicator from "@components/ui/indicators/RequiredFieldIndicator";

const COUNTRY_CODES = [
  "AF","AL","DZ","AD","AO","AG","AR","AM","AU","AT","AZ","BS","BH","BD","BB","BY","BE","BZ","BJ","BT",
  "BO","BA","BW","BR","BN","BG","BF","BI","CV","KH","CM","CA","CF","TD","CL","CN","CO","KM","CG","CD",
  "CR","CI","HR","CU","CY","CZ","DK","DJ","DM","DO","EC","EG","SV","GQ","ER","EE","SZ","ET","FJ","FI",
  "FR","GA","GM","GE","DE","GH","GR","GD","GT","GN","GW","GY","HT","HN","HU","IS","IN","ID","IR","IQ",
  "IE","IL","IT","JM","JP","JO","KZ","KE","KI","KP","KR","KW","KG","LA","LV","LB","LS","LR","LY","LI",
  "LT","LU","MG","MW","MY","MV","ML","MT","MH","MR","MU","MX","FM","MD","MC","MN","ME","MA","MZ","MM",
  "NA","NR","NP","NL","NZ","NI","NE","NG","MK","NO","OM","PK","PW","PA","PG","PY","PE","PH","PL","PT",
  "QA","RO","RU","RW","KN","LC","VC","WS","SM","ST","SA","SN","RS","SC","SL","SG","SK","SI","SB","SO",
  "ZA","SS","ES","LK","SD","SR","SE","CH","SY","TW","TJ","TZ","TH","TL","TG","TO","TT","TN","TR","TM",
  "TV","UG","UA","AE","GB","US","UY","UZ","VU","VA","VE","VN","YE","ZM","ZW"
];

/** Props for the country autocomplete select component. */
interface CountrySelectProps {
  name: string;
  value?: string;
  onChange?: (value: string, name: string) => void;
  required?: boolean;
  errors?: Set<string>;
  submitted?: boolean;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

function useCountries(t: (key: string) => string) {
  return useMemo(() =>
    COUNTRY_CODES.map(code => ({
      code,
      name: t(`countries.${code}`)
    })).sort((a, b) => a.name.localeCompare(b.name)),
    [t]
  );
}

function useDisplayValue(value: string, countries: { code: string; name: string }[]) {
  return useMemo(() => {
    if (!value) return "";
    const country = countries.find(c => c.code === value);
    return country ? country.name : value;
  }, [value, countries]);
}

function useClickOutsideClose2(
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  setIsOpen: (v: boolean) => void,
  setQuery: (v: string) => void,
  displayValue: string
) {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery(displayValue);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [displayValue, wrapperRef, setIsOpen, setQuery]);
}

const CountryDropdown: React.FC<{
  isOpen: boolean;
  filtered: { code: string; name: string }[];
  onChange?: (value: string, name: string) => void;
  name: string;
  setQuery: (v: string) => void;
  setIsOpen: (v: boolean) => void;
  highlightedIndex: number;
}> = ({ isOpen, filtered, onChange, name, setQuery, setIsOpen, highlightedIndex }) => {
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!listRef.current || highlightedIndex < 0) return;
    const item = listRef.current.children[highlightedIndex] as HTMLElement;
    if (item) item.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  if (!isOpen || filtered.length === 0) return null;
  return (
    <ul ref={listRef} className="absolute z-50 mt-1 max-h-48 w-full overflow-auto border border-[var(--border-medium)] bg-[var(--surface-primary)] shadow-lg text-sm">
      {filtered.map((c, idx) => (
        <li
          key={c.code}
          className={cn(
            "cursor-pointer px-3 py-2 text-[var(--text-primary)]",
            idx === highlightedIndex ? "bg-[var(--surface-hover)]" : "hover:bg-[var(--surface-hover)]"
          )}
          onMouseDown={() => {
            onChange?.(c.code, name);
            setQuery(c.name);
            setIsOpen(false);
          }}
        >
          {c.name}
        </li>
      ))}
    </ul>
  );
};

const CountryErrors: React.FC<{
  hasErrors: boolean;
  submitted: boolean;
  errors?: Set<string>;
}> = ({ hasErrors, submitted, errors }) => {
  if (!hasErrors || !submitted || !errors) return null;
  return (
    <div className="text-xs text-red-500 mt-1">
      {Array.from(errors).map((err, i) => <span key={i}>{err}</span>)}
    </div>
  );
};

function usePlaceholder(children: React.ReactNode): string {
  return typeof children === "string" ? children : "";
}

function useErrorState(errors: Set<string> | undefined, submitted: boolean | undefined): { hasErrors: boolean; showErrorBorder: boolean } {
  const hasErrors = !!(errors && errors.size > 0);
  const showErrorBorder = hasErrors && !!submitted;
  return { hasErrors, showErrorBorder };
}

function filterCountries(query: string, countries: { code: string; name: string }[]): { code: string; name: string }[] {
  if (!query) return countries;
  const lowerQuery = query.toLowerCase();
  return countries.filter(c => c.name.toLowerCase().includes(lowerQuery));
}

function resolveLabelColor(showErrorBorder: boolean): string {
  return showErrorBorder ? "text-red-600" : "text-[var(--text-primary)]";
}

const CountrySelect: React.FC<CountrySelectProps> = ({
  name, value = "", onChange, required, errors, submitted, children, disabled, className
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const countries = useCountries(t);
  const displayValue = useDisplayValue(value, countries);

  useEffect(() => {
    setQuery(displayValue);
  }, [displayValue]);

  const filtered = useMemo(() => filterCountries(query, countries), [query, countries]);

  useEffect(() => {
    setHighlightedIndex(filtered.length > 0 ? 0 : -1);
  }, [filtered]);

  useClickOutsideClose2(wrapperRef, setIsOpen, setQuery, displayValue);

  const placeholder = usePlaceholder(children);
  const { hasErrors, showErrorBorder } = useErrorState(errors, submitted);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < filtered.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filtered.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        const selected = filtered[highlightedIndex];
        onChange?.(selected.code, name);
        setQuery(selected.name);
        setIsOpen(false);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setQuery(displayValue);
    }
  };

  return (
    <div ref={wrapperRef} className={cn("relative w-full", className)}>
      <label
        className={`block text-sm leading-tight mb-1.5 ${resolveLabelColor(showErrorBorder)}`}
        htmlFor={name}
      >
        {placeholder}
        {required && <RequiredFieldIndicator required={required} className="ml-0.5" />}
      </label>
      <input
        name={name}
        id={name}
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete="off"
        className={cn(
          "auth-input w-full h-10 px-3 py-2 text-sm bg-[var(--surface-primary)] text-[var(--text-primary)] border border-[var(--border-medium)] focus:outline-none focus:border-[var(--seed-base)] placeholder:text-[var(--text-tertiary)] transition-colors duration-200",
          showErrorBorder && "border-red-500",
          disabled && "cursor-not-allowed opacity-50"
        )}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
      />
      <CountryDropdown
        isOpen={isOpen}
        filtered={filtered}
        onChange={onChange}
        name={name}
        setQuery={setQuery}
        setIsOpen={setIsOpen}
        highlightedIndex={highlightedIndex}
      />
      <CountryErrors hasErrors={hasErrors} submitted={!!submitted} errors={errors} />
    </div>
  );
};

export default CountrySelect;
